import React, { useEffect, useState, useCallback } from "react";
import { ShieldAlert, TrendingDown } from "lucide-react";
import { http } from "@/lib/api";
import { useOrg } from "@/context/OrgContext";
import { Card, Spinner } from "@/components/Kit";

const GRADE_C = { A: "#2E7D32", B: "#5E8B2A", C: "#E39629", D: "#D55816", E: "#A5300F" };
const gradeBg = (g) => ({ background: (GRADE_C[g] || "#8A8175") + "18", color: GRADE_C[g] || "#8A8175" });

function GradePill({ grade, score, testid }) {
  if (grade == null) return <span className="text-[#B5AC9E] text-xs">Not scored</span>;
  return (
    <span data-testid={testid} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold" style={gradeBg(grade)}>
      {grade}{score != null && <span className="opacity-70 font-semibold">· {score}</span>}
    </span>
  );
}

export default function RiskOverview() {
  const { orgId, org } = useOrg();
  const [rows, setRows] = useState(null);

  const load = useCallback(() => {
    if (!orgId) return;
    http.get("/attack/domains", { params: { org_id: orgId } }).then((r) => {
      const sorted = [...r.data].sort((a, b) => {
        const sa = a.risk_score == null ? 999 : a.risk_score;
        const sb = b.risk_score == null ? 999 : b.risk_score;
        return sa - sb; // weakest (lowest score) first
      });
      setRows(sorted);
    });
  }, [orgId]);
  useEffect(() => { setRows(null); }, [orgId]);
  useEffect(() => { load(); }, [load]);

  if (rows === null) return <div className="p-10 flex justify-center"><Spinner /></div>;

  const dist = { A: 0, B: 0, C: 0, D: 0, E: 0 };
  rows.forEach((d) => { if (d.risk_grade) dist[d.risk_grade]++; });
  const scored = rows.filter((d) => d.risk_score != null);
  const avg = scored.length ? Math.round(scored.reduce((s, d) => s + d.risk_score, 0) / scored.length) : null;
  const weakest = rows.filter((d) => d.risk_score != null && d.risk_score < 60);

  return (
    <div className="p-6 max-w-[1500px] mx-auto fade-up" data-testid="risk-overview">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: "#FFF5EB" }}>
          <ShieldAlert className="w-5 h-5 text-[#A5300F]" />
        </div>
        <div>
          <h1 className="font-display text-3xl font-bold text-[#A5300F]">Risk Overview</h1>
          <p className="text-sm text-[#8A8175]">{org?.name} portfolio · {rows.length} domains ranked weakest-first.</p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
        <Card className="p-4"><div className="text-[11px] uppercase tracking-wider text-[#8A8175] font-bold">Domains</div><div className="text-3xl font-display font-bold mt-1">{rows.length}</div></Card>
        <Card className="p-4"><div className="text-[11px] uppercase tracking-wider text-[#8A8175] font-bold">Average score</div><div className="text-3xl font-display font-bold mt-1" style={{ color: avg != null ? (avg >= 75 ? "#2E7D32" : avg >= 60 ? "#E39629" : "#A5300F") : "#B5AC9E" }}>{avg == null ? "—" : avg}</div></Card>
        <Card className="p-4"><div className="text-[11px] uppercase tracking-wider text-[#8A8175] font-bold">At risk (&lt;60)</div><div className="text-3xl font-display font-bold mt-1 text-[#A5300F]">{weakest.length}</div></Card>
        <Card className="p-4">
          <div className="text-[11px] uppercase tracking-wider text-[#8A8175] font-bold">Grade spread</div>
          <div className="flex gap-1.5 mt-2">
            {["A", "B", "C", "D", "E"].map((g) => (
              <div key={g} className="flex-1 text-center">
                <div className="text-lg font-bold" style={{ color: GRADE_C[g] }}>{dist[g]}</div>
                <div className="text-[10px] font-bold" style={{ color: GRADE_C[g] }}>{g}</div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <Card className="mt-6 overflow-hidden">
        <div className="px-4 py-3 border-b border-[#ECE6DD] flex items-center gap-2 text-sm font-semibold text-[#5C554C]">
          <TrendingDown className="w-4 h-4 text-[#A5300F]" /> Domains ranked by composite risk (weakest first)
        </div>
        <table className="w-full text-sm" data-testid="risk-table">
          <thead><tr className="text-[11px] uppercase tracking-wider text-[#8A8175] text-left bg-[#FFF5EB] border-b border-[#ECE6DD]">
            <th className="py-2.5 px-4 w-8">#</th><th>Domain</th><th className="text-center">Risk</th><th className="text-center">Email</th>
            <th className="text-center">Spoofability</th><th className="text-center">Assets</th><th className="text-center">Takeovers</th>
          </tr></thead>
          <tbody>
            {rows.map((d, i) => (
              <tr key={d.id} className="border-b border-[#F4EFE8] hover:bg-[#FBF7F1]" data-testid={`risk-row-${d.registrable_domain}`}>
                <td className="py-2.5 px-4 text-[#B5AC9E] font-mono text-xs">{i + 1}</td>
                <td className="font-semibold text-[#323232]">{d.registrable_domain}</td>
                <td className="text-center"><GradePill grade={d.risk_grade} score={d.risk_score} testid={`risk-grade-${d.registrable_domain}`} /></td>
                <td className="text-center"><GradePill grade={d.email_grade} score={d.email_score} /></td>
                <td className="text-center text-xs">
                  {d.spoofability ? <span className="font-semibold" style={{ color: d.spoofability === "protected" ? "#2E7D32" : d.spoofability === "partial" ? "#E39629" : "#A5300F" }}>{d.spoofability}</span> : <span className="text-[#B5AC9E]">—</span>}
                </td>
                <td className="text-center text-[#5C554C]">{d.asset_count ?? "—"}</td>
                <td className="text-center">{d.takeover_count ? <span className="text-[#A5300F] font-bold">{d.takeover_count}</span> : <span className="text-[#B5AC9E]">0</span>}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={7} className="py-10 text-center text-[#B5AC9E]">No domains in this organisation.</td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
